import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Paper, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, Alert, Typography, Chip, InputAdornment, Tooltip,
  FormControl, InputLabel, Select, Stack, Divider, Autocomplete, useMediaQuery, useTheme,
  Menu, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  Add, Edit, Search, Delete, Inventory2, CalendarMonth, Visibility,
  KeyboardArrowDown, Check,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';

const STATUS_OPTIONS = [
  { v: 'draft',           label: 'Draft',            color: 'default' },
  { v: 'quote_requested', label: 'Quote Requested',  color: 'info' },
  { v: 'quoted',          label: 'Quoted',           color: 'primary' },
  { v: 'negotiating',     label: 'Negotiating',      color: 'warning' },
  { v: 'ordered',         label: 'Ordered',          color: 'secondary' },
  { v: 'shipped',         label: 'Shipped',          color: 'info' },
  { v: 'received',        label: 'Received',         color: 'success' },
  { v: 'cancelled',       label: 'Cancelled',        color: 'error' },
];
const PRIORITY_OPTIONS = [
  { v: 'urgent', label: 'Urgent', color: 'error' },
  { v: 'high',   label: 'High',   color: 'warning' },
  { v: 'medium', label: 'Medium', color: 'primary' },
  { v: 'low',    label: 'Low',    color: 'default' },
];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CNY', 'HKD', 'JPY'];
const UOM        = ['pcs', 'kg', 'g', 'm', 'roll', 'box', 'set'];

const statusChip = (s) => {
  const o = STATUS_OPTIONS.find(x => x.v === s);
  return <Chip size="small" label={o?.label || s} color={o?.color || 'default'} variant={o?.color === 'default' ? 'outlined' : 'filled'} />;
};
const priorityChip = (s) => {
  const o = PRIORITY_OPTIONS.find(x => x.v === s);
  return <Chip size="small" label={o?.label || s} color={o?.color || 'default'} />;
};

const fmt = (v) => v == null || v === '' ? '—' : v;
const num   = (v) => v == null || v === '' || isNaN(Number(v)) ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 });
// Quantity formatter — integer only (no decimals)
const qty   = (v) => v == null || v === '' || isNaN(Number(v)) ? '—' : Math.round(Number(v)).toLocaleString('en-US');
// Unit-price formatter — shows 2–4 decimals (e.g. 0.03 stays 0.03, 0.075 stays 0.075, 0.07505 → 0.0751)
const price = (v) => v == null || v === '' || isNaN(Number(v)) ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

// Compact label/value row used in mobile cards
const Row = ({ label, value, valueColor, valueWeight }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontSize: 11 }}>{label}</Typography>
    <Typography variant="body2" sx={{
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      minWidth: 0, textAlign: 'right', fontSize: 13,
      color: valueColor, fontWeight: valueWeight,
    }}>
      {value}
    </Typography>
  </Box>
);

const EMPTY = {
  product_name: '', product_description: '', specifications: '',
  quantity: '', unit_of_measure: 'pcs', target_unit_price: '', target_currency: 'USD',
  beneficiary_id: '', sourcing_supplier_id: '', required_by: '',
  priority: 'medium', status: 'draft',
  quoted_unit_price: '', quoted_currency: 'USD', quoted_moq: '',
  quoted_lead_time_days: '', quoted_payment_terms: '', quoted_at: '',
  po_number: '', ordered_at: '', expected_delivery: '', notes: '',
};

export default function OrdersToPlace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [list, setList]                       = useState([]);
  const [stats, setStats]                     = useState(null);
  const [search, setSearch]                   = useState('');
  const [filterStatus, setFilterStatus]       = useState('');
  const [filterPriority, setFilterPriority]   = useState('');
  const [filterOverdue, setFilterOverdue]     = useState(false);
  const [dialog, setDialog]                   = useState(false);
  const [editing, setEditing]                 = useState(null);
  const [form, setForm]                       = useState(EMPTY);
  const [error, setError]                     = useState('');
  const [loading, setLoading]                 = useState(false);
  const [beneficiaries, setBeneficiaries]     = useState([]);
  const [sourcingSuppliers, setSourcingSuppliers] = useState([]);
  const [statusMenuAnchor, setStatusMenuAnchor]   = useState(null);
  const [statusMenuOrder,  setStatusMenuOrder]    = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search)          params.append('search', search);
      if (filterStatus)    params.append('status', filterStatus);
      if (filterPriority)  params.append('priority', filterPriority);
      if (filterOverdue)   params.append('overdue', 'true');
      const r = await api.get(`/sourcing-orders?${params}`);
      setList(r.data.data || []);
      const s = await api.get('/sourcing-orders/stats');
      setStats(s.data);
    } catch (e) { console.error(e); }
  }, [search, filterStatus, filterPriority, filterOverdue]);
  useEffect(() => { load(); }, [load]);

  // ── Deep-link: ?edit=ID auto-opens the edit dialog after list loads ──
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || !list.length) return;
    const found = list.find(o => String(o.id) === String(editId));
    if (found) {
      openEdit(found);
      // Clear the param so refreshing doesn't re-open
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }

  }, [list]);

  // Load supplier dropdowns once
  useEffect(() => {
    api.get('/beneficiaries').then(r => setBeneficiaries(r.data.data || []));
    api.get('/sourcing-suppliers').then(r => setSourcingSuppliers(r.data.data || []));
  }, [dialog]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setError(''); setDialog(true); };
  const openEdit   = (o) => {
    setEditing(o);
    // Strip trailing zeros from numeric fields so inputs don't show "800000.0000"
    const cleanQty   = (v) => (v == null || v === '') ? '' : String(Math.round(parseFloat(v)));
    const cleanPrice = (v) => (v == null || v === '') ? '' : String(parseFloat(v));
    const cleanInt   = (v) => (v == null || v === '') ? '' : String(parseInt(v));
    setForm({
      ...EMPTY, ...o,
      quantity:              cleanQty(o.quantity),
      quoted_moq:            cleanQty(o.quoted_moq),
      target_unit_price:     cleanPrice(o.target_unit_price),
      quoted_unit_price:     cleanPrice(o.quoted_unit_price),
      quoted_lead_time_days: cleanInt(o.quoted_lead_time_days),
      required_by:       o.required_by      ? o.required_by.substring(0, 10)      : '',
      quoted_at:         o.quoted_at        ? o.quoted_at.substring(0, 10)        : '',
      ordered_at:        o.ordered_at       ? o.ordered_at.substring(0, 10)       : '',
      expected_delivery: o.expected_delivery ? o.expected_delivery.substring(0, 10) : '',
    });
    setError(''); setDialog(true);
  };

  const handleSave = async () => {
    if (!form.product_name) { setError('Product name is required'); return; }
    setLoading(true);
    try {
      // Convert empty strings to null for numeric/date fields
      const payload = { ...form };
      ['quantity','target_unit_price','quoted_unit_price','quoted_moq','quoted_lead_time_days',
       'beneficiary_id','sourcing_supplier_id'].forEach(k => {
        if (payload[k] === '' || payload[k] == null) payload[k] = null;
        else if (k.includes('id') || k === 'quoted_lead_time_days') payload[k] = parseInt(payload[k]);
        else payload[k] = parseFloat(payload[k]);
      });
      if (editing) await api.put(`/sourcing-orders/${editing.id}`, payload);
      else         await api.post('/sourcing-orders', payload);
      setDialog(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving');
    } finally { setLoading(false); }
  };

  const handleDelete = async (o) => {
    if (!window.confirm(`Delete order "${o.order_ref || o.product_name}"?`)) return;
    await api.delete(`/sourcing-orders/${o.id}`);
    load();
  };

  const openStatusMenu = (e, order) => {
    e.stopPropagation();
    setStatusMenuAnchor(e.currentTarget);
    setStatusMenuOrder(order);
  };
  const closeStatusMenu = () => { setStatusMenuAnchor(null); setStatusMenuOrder(null); };
  const updateStatus = async (newStatus) => {
    if (!statusMenuOrder || statusMenuOrder.status === newStatus) { closeStatusMenu(); return; }
    try {
      await api.put(`/sourcing-orders/${statusMenuOrder.id}`, { status: newStatus });
      closeStatusMenu();
      load();
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const f = (field) => ({ value: form[field] ?? '', onChange: (e) => setForm({ ...form, [field]: e.target.value }) });
  const supplierLabel = (o) =>
    o.beneficiary_id      ? `${o.beneficiary_name} (active)` :
    o.sourcing_supplier_id ? `${o.sourcing_supplier_name} (sourcing)` : '—';

  return (
    <Box>
      <PageHeader title="Orders to Place" subtitle="Procurement pipeline — items to source from suppliers"
        action={<Button variant="contained" startIcon={<Add />} onClick={openCreate}>New Order</Button>}
      />

      {/* Summary cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>
            <Card elevation={1} sx={{ borderRadius: 2 }}><CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Pipeline value</Typography>
              <Typography variant="h6" fontWeight={700}>${num(stats.pipeline_value)}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card elevation={1} sx={{ borderRadius: 2, bgcolor: stats.overdue ? 'error.50' : undefined }}><CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Overdue</Typography>
              <Typography variant="h6" fontWeight={700} color={stats.overdue ? 'error.main' : undefined}>{stats.overdue}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ borderRadius: 2 }}><CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Active by Status</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {stats.by_status.filter(s => !['received','cancelled'].includes(s.status)).map(s =>
                  <Chip key={s.status} size="small"
                    label={`${STATUS_OPTIONS.find(x => x.v === s.status)?.label || s.status}: ${s.c}`}
                    color={STATUS_OPTIONS.find(x => x.v === s.status)?.color || 'default'}
                    variant="outlined" />
                )}
              </Stack>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField size="small" placeholder="Search product, ref, PO…" value={search} onChange={e => setSearch(e.target.value)} sx={{ flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map(s => <MenuItem key={s.v} value={s.v}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Priority</InputLabel>
          <Select value={filterPriority} label="Priority" onChange={e => setFilterPriority(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {PRIORITY_OPTIONS.map(s => <MenuItem key={s.v} value={s.v}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant={filterOverdue ? 'contained' : 'outlined'} color="error" size="small"
          startIcon={<CalendarMonth />} onClick={() => setFilterOverdue(!filterOverdue)}>
          Overdue Only
        </Button>
      </Stack>

      {isMobile ? (
        // ── Mobile card view ───────────────────────────────────────────────
        list.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            No orders. Tap "New Order" to start tracking your procurement pipeline.
          </Paper>
        ) : (
          <Stack spacing={1.25}>
            {list.map(o => {
              const overdue = o.days_to_deadline != null && o.days_to_deadline < 0 && !['received','cancelled'].includes(o.status);
              const unitPrice = o.quoted_unit_price || o.target_unit_price;
              const totalValue = unitPrice && o.quantity ? parseFloat(unitPrice) * parseFloat(o.quantity) : null;
              const valueCcy = o.quoted_currency || o.target_currency || 'USD';
              return (
                <Paper key={o.id} elevation={0}
                  onClick={() => navigate(`/sourcing/orders/${o.id}`)}
                  sx={{
                    p: 1.5, border: '1px solid', borderColor: overdue ? 'error.light' : 'divider',
                    borderLeft: '4px solid', borderLeftColor: overdue ? 'error.main' :
                      o.priority === 'urgent' ? 'error.main' :
                      o.priority === 'high'   ? 'warning.main' :
                      o.priority === 'medium' ? 'primary.main' : 'grey.300',
                    cursor: 'pointer', touchAction: 'manipulation',
                    '&:active': { bgcolor: 'action.hover' },
                  }}>
                  {/* Top: ref + product + clickable status */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: 10.5, lineHeight: 1.2 }}>{o.order_ref}</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: 0.25 }}>
                        {o.product_name}
                      </Typography>
                    </Box>
                    <Box onClick={(e) => openStatusMenu(e, o)} sx={{ flexShrink: 0 }}>
                      {React.cloneElement(statusChip(o.status), { onDelete: (e) => openStatusMenu(e, o), deleteIcon: <KeyboardArrowDown sx={{ fontSize: 16 }} /> })}
                    </Box>
                  </Box>

                  {/* Pills row */}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5, mb: 1 }}>
                    {priorityChip(o.priority)}
                    {o.required_by && (
                      <Chip size="small" variant="outlined"
                        color={overdue ? 'error' : o.days_to_deadline < 7 ? 'warning' : 'default'}
                        label={o.days_to_deadline != null && !['received','cancelled'].includes(o.status)
                          ? (o.days_to_deadline < 0 ? `${Math.abs(o.days_to_deadline)}d overdue` : `${o.days_to_deadline}d left`)
                          : o.required_by.substring(0, 10)} />
                    )}
                  </Stack>

                  {/* Total order value — prominent */}
                  {totalValue != null && (
                    <Box sx={{ p: 1, mb: 1, bgcolor: 'rgba(26, 58, 219, 0.10)', borderRadius: 1, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                        Total Order Value
                      </Typography>
                      <Typography variant="body1" fontWeight={700} color="primary.dark">
                        {valueCcy} {num(totalValue)}
                      </Typography>
                    </Box>
                  )}

                  {/* Detail rows */}
                  <Box sx={{ display: 'grid', rowGap: 0.5, columnGap: 1 }}>
                    <Row label="Quantity" value={`${qty(o.quantity)} ${o.unit_of_measure || ''}`} />
                    {o.target_unit_price && <Row label="Target Price" value={`${o.target_currency} ${price(o.target_unit_price)}`} />}
                    {o.quoted_unit_price && <Row label="Quoted Price"
                      value={`${o.quoted_currency || o.target_currency} ${price(o.quoted_unit_price)}`} valueColor="primary.main" valueWeight={600} />}
                    <Row label="Supplier" value={supplierLabel(o)} />
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, pt: 0.75, borderTop: '1px solid', borderColor: 'divider', gap: 0.25 }}
                    onClick={e => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => openEdit(o)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(o)}><Delete fontSize="small" /></IconButton>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )
      ) : (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
              <TableCell>Ref</TableCell>
              <TableCell>Product</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Target Price</TableCell>
              <TableCell align="right">Quoted</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Required</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                No orders. Create one to start tracking your procurement pipeline.
              </TableCell></TableRow>
            ) : list.map(o => (
              <TableRow key={o.id} hover sx={{ bgcolor: o.days_to_deadline != null && o.days_to_deadline < 0 && !['received','cancelled'].includes(o.status) ? 'error.50' : undefined }}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{o.order_ref}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{o.product_name}</Typography>
                  {o.product_description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {o.product_description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{qty(o.quantity)} {o.unit_of_measure || ''}</TableCell>
                <TableCell align="right">{o.target_unit_price ? `${o.target_currency} ${price(o.target_unit_price)}` : '—'}</TableCell>
                <TableCell align="right">{o.quoted_unit_price ? `${o.quoted_currency || o.target_currency} ${price(o.quoted_unit_price)}` : '—'}</TableCell>
                <TableCell sx={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{supplierLabel(o)}</TableCell>
                <TableCell>
                  {o.required_by ? (
                    <Box>
                      <Typography variant="body2" fontSize={12}>{o.required_by.substring(0, 10)}</Typography>
                      {o.days_to_deadline != null && !['received','cancelled'].includes(o.status) && (
                        <Typography variant="caption" color={o.days_to_deadline < 0 ? 'error.main' : o.days_to_deadline < 7 ? 'warning.main' : 'text.secondary'}>
                          {o.days_to_deadline < 0 ? `${Math.abs(o.days_to_deadline)}d overdue` : `${o.days_to_deadline}d left`}
                        </Typography>
                      )}
                    </Box>
                  ) : '—'}
                </TableCell>
                <TableCell>{priorityChip(o.priority)}</TableCell>
                <TableCell onClick={(e) => openStatusMenu(e, o)} sx={{ cursor: 'pointer' }}>
                  {React.cloneElement(statusChip(o.status), { onDelete: (e) => openStatusMenu(e, o), deleteIcon: <KeyboardArrowDown sx={{ fontSize: 16 }} /> })}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View Details"><IconButton size="small" color="info" onClick={() => navigate(`/sourcing/orders/${o.id}`)}><Visibility fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(o)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(o)}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialog}
        onClose={(_, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') setDialog(false); }}
        fullWidth maxWidth="md">
        <DialogTitle>{editing ? `Edit Order ${editing.order_ref}` : 'New Order'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Product details</Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} md={8}><TextField fullWidth required label="Product Name" {...f('product_name')} /></Grid>
            <Grid item xs={6} md={4}>
              <TextField select fullWidth label="Priority" {...f('priority')}>
                {PRIORITY_OPTIONS.map(o => <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Description" {...f('product_description')} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Specifications" placeholder="Size, model, frequency, etc." {...f('specifications')} /></Grid>
            <Grid item xs={4}><TextField fullWidth type="number" label="Quantity" {...f('quantity')} /></Grid>
            <Grid item xs={4}>
              <TextField select fullWidth label="UOM" {...f('unit_of_measure')}>
                {UOM.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4}><TextField fullWidth type="date" label="Required By" InputLabelProps={{ shrink: true }} {...f('required_by')} /></Grid>
            <Grid item xs={6} md={4}><TextField fullWidth type="number" label="Target Unit Price" {...f('target_unit_price')} /></Grid>
            <Grid item xs={6} md={4}>
              <TextField select fullWidth label="Currency" {...f('target_currency')}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Status" {...f('status')}>
                {STATUS_OPTIONS.map(s => <MenuItem key={s.v} value={s.v}>{s.label}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }}><Typography variant="caption" color="text.secondary">Supplier</Typography></Divider>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete size="small"
                options={beneficiaries}
                getOptionLabel={(o) => o.name || ''}
                value={beneficiaries.find(b => b.id === parseInt(form.beneficiary_id)) || null}
                onChange={(_, v) => setForm({ ...form, beneficiary_id: v?.id || '', sourcing_supplier_id: v ? '' : form.sourcing_supplier_id })}
                renderInput={(params) => <TextField {...params} label="Active Beneficiary (existing supplier)" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete size="small"
                options={sourcingSuppliers}
                getOptionLabel={(o) => o.company_name || ''}
                value={sourcingSuppliers.find(b => b.id === parseInt(form.sourcing_supplier_id)) || null}
                onChange={(_, v) => setForm({ ...form, sourcing_supplier_id: v?.id || '', beneficiary_id: v ? '' : form.beneficiary_id })}
                renderInput={(params) => <TextField {...params} label="Sourcing Candidate (new supplier)" />}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }}><Typography variant="caption" color="text.secondary">Quote received (after RFQ)</Typography></Divider>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><TextField fullWidth type="number" label="Quoted Price" {...f('quoted_unit_price')} /></Grid>
            <Grid item xs={6} md={3}>
              <TextField select fullWidth label="Currency" {...f('quoted_currency')}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={3}><TextField fullWidth type="number" label="MOQ" {...f('quoted_moq')} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth type="number" label="Lead Time (days)" {...f('quoted_lead_time_days')} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Payment Terms" placeholder="30% T/T advance, 70% before shipment" {...f('quoted_payment_terms')} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth type="date" label="Quote Date" InputLabelProps={{ shrink: true }} {...f('quoted_at')} /></Grid>
          </Grid>

          <Divider sx={{ my: 2 }}><Typography variant="caption" color="text.secondary">Order placed</Typography></Divider>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}><TextField fullWidth label="PO Number" {...f('po_number')} /></Grid>
            <Grid item xs={6} md={4}><TextField fullWidth type="date" label="Order Date" InputLabelProps={{ shrink: true }} {...f('ordered_at')} /></Grid>
            <Grid item xs={6} md={4}><TextField fullWidth type="date" label="Expected Delivery" InputLabelProps={{ shrink: true }} {...f('expected_delivery')} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Notes" {...f('notes')} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Quick status update menu */}
      <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={closeStatusMenu}>
        <Box sx={{ px: 2, py: 1, fontSize: 11, color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Update status
        </Box>
        {STATUS_OPTIONS.map(s => (
          <MenuItem key={s.v} onClick={() => updateStatus(s.v)} selected={statusMenuOrder?.status === s.v} sx={{ py: 1 }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              {statusMenuOrder?.status === s.v ? <Check fontSize="small" color="primary" /> : <Box sx={{ width: 18 }} />}
            </ListItemIcon>
            <ListItemText>{s.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
